import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const Users = () => {
  return (
    <section className="section">
      <h2 className="section-title">Users</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        User management and authentication for Drawbridge API. Create a user account to receive your API key,
        which is required for authenticating all other API requests. User accounts enable access control
        and namespace permissions throughout the platform.
      </p>

      <EndpointCard
        method="POST"
        path="/users/"
        title="Create User"
        description="Creates a new user and returns an API key. Password is optional - users can be created for API-only access or with password for console login. This is the only endpoint that doesn't require authentication."
        tags={['Authentication: Not Required', 'Idempotent: Yes']}
        requestBody={{ email: 'user@example.com', password: 'optional_password123' }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "email": "user@example.com",
  "password": "optional_password123"  // Optional
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>email</strong> (required): User's email address<br/>
              <strong>password</strong> (optional): If provided, enables password-based login through the console. If omitted, user can only authenticate via API key.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example (with password):</h4>
            <CodeBlock code={`curl -X POST http://localhost:8000/api/v1/users/ \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "password": "securepassword123"
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example (passwordless - API-only):</h4>
            <CodeBlock code={`curl -X POST http://localhost:8000/api/v1/users/ \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "api-user@example.com"
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "email": "user@example.com",
  "uuid": "4d9804ff-fd89-4c70-9ef6-68fbdf109f6d",
  "api_key": "BEKn4V_JpFrociauurlJCPwjf_X3ui5vGJYOETo1eaA"
}`} />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>User Flows:</strong><br/>
              • <strong>API-only users:</strong> Create without password, authenticate via API key only. Can optionally enable password later via <code className="bg-blue-900/50 px-1 py-0.5 rounded">/auth/enable-password</code><br/>
              • <strong>Console users:</strong> Create with password, can login through frontend console and get API key via <code className="bg-blue-900/50 px-1 py-0.5 rounded">/auth/login</code>
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/users/"
        title="List Users"
        description="Retrieves a paginated list of all users in the system. Results are ordered by creation date (most recent first)."
        tags={['Authentication: Required', 'Pagination: Supported']}
        parameters={{
          query: {
            skip: { required: false, description: 'Number of records to skip for pagination', default: 0 },
            limit: { required: false, description: 'Maximum number of records to return (max: 100)', default: 100 }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Query Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Default</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>skip</code></td>
                  <td>integer</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>0</td>
                  <td>Number of records to skip for pagination</td>
                </tr>
                <tr>
                  <td><code>limit</code></td>
                  <td>integer</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>100</td>
                  <td>Maximum number of records to return (max: 100)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "http://localhost:8000/api/v1/users/?skip=0&limit=10" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/users/{user_id}"
        title="Get User"
        description="Retrieves detailed information about a specific user by their UUID."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            user_id: { required: true, description: 'The unique identifier of the user' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>user_id</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the user</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "http://localhost:8000/api/v1/users/4d9804ff-fd89-4c70-9ef6-68fbdf109f6d" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="PUT"
        path="/users/{user_id}"
        title="Update User"
        description="Updates user information. Only the email can be updated."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            user_id: { required: true, description: 'The unique identifier of the user' }
          }
        }}
        requestBody={{ email: 'newemail@example.com' }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "email": "newemail@example.com"
}`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X PUT "http://localhost:8000/api/v1/users/{user_id}" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "newemail@example.com"
  }'`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="DELETE"
        path="/users/{user_id}"
        title="Delete User"
        description="Permanently deletes a user account and all associated data."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            user_id: { required: true, description: 'The unique identifier of the user to delete' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>user_id</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the user to delete</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X DELETE "http://localhost:8000/api/v1/users/4d9804ff-fd89-4c70-9ef6-68fbdf109f6d" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "email": "user@example.com",
  "uuid": "4d9804ff-fd89-4c70-9ef6-68fbdf109f6d"
}`} />
          </div>
        </div>
      </EndpointCard>

      <h3 className="text-xl font-semibold text-white mt-12 mb-6">Authentication</h3>

      <EndpointCard
        method="POST"
        path="/auth/login"
        title="Login with Password"
        description="Login with email and password to get an API key. Only available for users created with password or who enabled password via /auth/enable-password."
        tags={['Authentication: Not Required']}
        requestBody={{ email: 'user@example.com', password: 'password123' }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "email": "user@example.com",
  "password": "password123"
}`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST http://localhost:8000/api/v1/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "password": "password123"
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "email": "user@example.com",
  "uuid": "4d9804ff-fd89-4c70-9ef6-68fbdf109f6d",
  "api_key": "5sok9UF33W18VlOsrigrAoJOEgPyfsqrkwQWXdbG0eY",
  "message": "Login successful. Use this API key for subsequent API requests."
}`} />
          </div>

          <div className="p-4 bg-orange-900/20 border border-orange-700/50 rounded-lg">
            <p className="text-sm text-orange-200">
              <strong>Note:</strong> This endpoint only works for users with password authentication enabled. Passwordless users (created without password) will receive a 401 error with message "This account uses API key authentication only."
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/auth/enable-password"
        title="Enable Password Authentication"
        description="Enable password authentication for passwordless users. This allows API-only users to login through the console. This is a one-time operation - once enabled, password authentication cannot be disabled."
        tags={['Authentication: Required']}
        requestBody={{ password: 'newsecurepassword123' }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "password": "newsecurepassword123"
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>password</strong> (required): New password for the account (minimum 8 characters)
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST http://localhost:8000/api/v1/auth/enable-password \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "password": "newsecurepassword123"
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "message": "Password authentication enabled successfully"
}`} />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200 mb-3">
              <strong>Use Case:</strong> If you created a user via the API without a password, use this endpoint to enable password-based login through the console (frontend).
            </p>
            <p className="text-sm text-blue-200">
              <strong>Note:</strong> This is a one-time operation. Once password authentication is enabled, you cannot disable it. If the user already has password authentication enabled, this endpoint will return a 403 error.
            </p>
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default Users;
